import { Box, Typography } from '@mui/material';
import { DateUtils } from '../../../utils';
import { TravelerOrderHeader as OrderHeaderData } from '../types';

interface TravelerOrderHeaderProps {
  order: OrderHeaderData;
}

const fieldLabelSx = { fontWeight: 500, fontSize: '13px', color: '#333' };
const fieldValueSx = { fontSize: '13px', color: '#333' };
const highlightSx = { ...fieldValueSx, color: 'primary.main', fontWeight: 600 };
const urgentSx = { ...fieldValueSx, color: 'error.main', fontWeight: 600 };

const TravelerOrderHeader = ({ order }: TravelerOrderHeaderProps) => {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        p: '16px 20px',
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.5fr',
          gap: 3,
        }}
      >
        {/* Left: Order Details Box */}
        <Box
          sx={{
            border: '1px solid #999',
            p: '12px 16px',
            bgcolor: '#fafafa',
            position: 'relative',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#666',
              textTransform: 'uppercase',
              bgcolor: '#fafafa',
              display: 'inline-block',
              px: 0.5,
              mt: '-20px',
              mb: 1,
            }}
          >
            Order Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '4px 12px',
            }}
          >
            <Typography sx={fieldLabelSx}>Order #:</Typography>
            <Typography sx={highlightSx}>{order.orderNumber}</Typography>
            <Typography sx={fieldLabelSx}>Customer Name:</Typography>
            <Typography sx={highlightSx}>{order.customerName}</Typography>
            <Typography sx={fieldLabelSx}>Order Date:</Typography>
            <Typography sx={fieldValueSx}>{order.orderDate}</Typography>
            <Typography sx={fieldLabelSx}>Order Due Date:</Typography>
            <Typography sx={fieldValueSx}>
              {order.dueDate ?? '—'}
              {order.dueDateChangedAt && (
                <Typography
                  component="span"
                  sx={{ fontSize: '11px', color: 'text.secondary', ml: 0.5 }}
                >
                  (updated {DateUtils.toDisplayDateTime(order.dueDateChangedAt)})
                </Typography>
              )}
            </Typography>
            <Typography sx={fieldLabelSx}>PO#:</Typography>
            <Typography sx={highlightSx}>{order.po}</Typography>
            <Typography sx={fieldLabelSx}>Job Name:</Typography>
            <Typography sx={fieldValueSx}>{order.jobName}</Typography>
          </Box>
        </Box>

        {/* Right: Delivery / Logistics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '6px 16px',
            alignContent: 'start',
          }}
        >
          <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Delivery Options:</Typography>
          <Typography sx={fieldValueSx}>{order.deliveryOption}</Typography>

          <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Location:</Typography>
          <Typography sx={fieldValueSx}>{order.location}</Typography>

          <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Due Date:</Typography>
          <Typography sx={urgentSx}>
            {order.dueDate ?? '—'}
            {order.dueDateChangedAt && (
              <Typography
                component="span"
                sx={{ fontSize: '11px', color: 'text.secondary', ml: 0.5, fontWeight: 400 }}
              >
                (updated {DateUtils.toDisplayDateTime(order.dueDateChangedAt)})
              </Typography>
            )}
          </Typography>

          {order.notes && (
            <Box sx={{ gridColumn: '1 / -1', mt: 0.5 }}>
              <Typography sx={{ ...fieldLabelSx, fontWeight: 600, mb: 0.25 }}>Notes:</Typography>
              <Typography sx={fieldValueSx}>{order.notes}</Typography>
            </Box>
          )}

          {(order.signedBy || order.signedDate) && (
            <Box
              sx={{
                gridColumn: '1 / -1',
                display: 'flex',
                gap: 3,
                mt: 1,
                pt: 1,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              {order.signedBy && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Signed:</Typography>
                  <Typography sx={fieldValueSx}>{order.signedBy}</Typography>
                </Box>
              )}
              {order.signedDate && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ ...fieldLabelSx, fontWeight: 600 }}>Date:</Typography>
                  <Typography sx={fieldValueSx}>{order.signedDate}</Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TravelerOrderHeader;
